import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineBusIcon from 'LuxKit/Icons/line/LineBusIcon'
import LineCarIcon from 'LuxKit/Icons/line/LineCarIcon'
import LineCompassIcon from 'LuxKit/Icons/line/LineCompassIcon'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import LineMapPinIcon from 'LuxKit/Icons/line/LineMapPinIcon'
import LineNotesIcon from 'LuxKit/Icons/line/LineNotesIcon'
import LinePlaneFlyIcon from 'LuxKit/Icons/line/LinePlaneFlyIcon'
import LineRestaurantIcon from 'LuxKit/Icons/line/LineRestaurantIcon'
import LineShipIcon from 'LuxKit/Icons/line/LineShipIcon'
import LineSubwayIcon from 'LuxKit/Icons/line/LineSubwayIcon'
import LineTicketIcon from 'LuxKit/Icons/line/LineTicketIcon'
import LineUniversityIcon from 'LuxKit/Icons/line/LineUniversityIcon'
import type { SvgIconComponent } from 'LuxKit/SvgIcon'
import LeIcon from 'tripPlanner/components/Icons/LeIcon'
import type {
  ItemType,
  LETripItem,
  ParentItemType,
} from 'tripPlanner/types/tripItem'
import { defineMessages, type MessageDescriptor } from 'react-intl'
import productLabels from 'i18n/productLabels'

interface ParentItemViewModel {
  name: MessageDescriptor
  icon: SvgIconComponent
  description?: MessageDescriptor
}

interface ItemViewModel {
  name: MessageDescriptor
  singularName: MessageDescriptor
  icon: SvgIconComponent
  editable: boolean
  labels?: {
    place?: MessageDescriptor
    phone?: MessageDescriptor
    confirmation?: MessageDescriptor
    url?: MessageDescriptor
    notes?: MessageDescriptor
  }
}

const parentItemMessages = defineMessages({
  leBookingsName: {
    id: 'ATK9G-hReINRseeQ-zLo',
    defaultMessage: 'My Bookings',
    description: 'Parent item type name for Luxury Escapes bookings in trip planner',
  },
  placeName: {
    id: '7kxBqfUauTN27dsgL76Q',
    defaultMessage: 'Place',
    description: 'Parent item type name for places in trip planner in the trip planner',
  },
  placeDescription: {
    id: 'OQ0UrkTa_ZQ-3u0gWmPW',
    defaultMessage: 'Attractions, restaurants, activities and more',
    description: 'Description for the Place parent item type in trip planner',
  },
  tourAndCruisesDescription: {
    id: 'Zi0empDUxRJf3wHxfdEQ',
    defaultMessage: 'Multi-day guided experiences',
    description: 'Description for Tours & Cruises parent item type in trip planner',
  },
  tourDescription: {
    id: 'dR1dAn4EDPvGbFD_rLCe',
    defaultMessage: 'Day or multi-day tours',
    description: 'Description for Tour parent item type in trip planner',
  },
  carRentalName: {
    id: 'MXqBD0kqN6JftrDLtMlh',
    defaultMessage: 'Car Rental',
    description: 'Parent item type name for car rental in trip planner',
  },
  carRentalDescription: {
    id: '07NI6ECXM_C15cMuqJMJ',
    defaultMessage: 'Renting private vehicles',
    description: 'Description for Car Rental parent item type in trip planner',
  },
  transportName: {
    id: 'HUMiHUZvCCrZNG7KmjWj',
    defaultMessage: 'Transport',
    description: 'Parent item type name for transport in trip planner',
  },
  transportDescription: {
    id: '5qiWarkfUbutIiB5lJ6n',
    defaultMessage: 'Car rental, rail, bus, ferry',
    description: 'Description for Transport parent item type in trip planner',
  },
  noteName: {
    id: 'g-JRdFiqVHgTz-EfAL3Q',
    defaultMessage: 'Note',
    description: 'Parent item type name for notes in trip planner in the trip planner',
  },
})

const labelMessages = defineMessages({
  additionalNotes: {
    id: 'YWF2AVEeg3rmZPU7UYRZ',
    defaultMessage: 'Additional notes',
    description: 'Label for additional notes field on trip planner item',
  },
  placeName: {
    id: '0ip8aJIfyYfSkHCN6XJv',
    defaultMessage: 'Place name',
    description: 'Label for place name field on accommodation trip item',
  },
  phoneNumber: {
    id: 'HC_eZCDxq1FAZNR2OfrQ',
    defaultMessage: 'Phone number',
    description: 'Form field label for phone number field on trip planner item',
  },
  confirmationCode: {
    id: 'prW-Mm6-lUiAopUzEwcN',
    defaultMessage: 'Confirmation code',
    description: 'Label for confirmation/booking code field on trip planner item',
  },
  name: {
    id: 'LTQ9Y0ILKPtUhI0XeUk8',
    defaultMessage: 'Name',
    description: 'Form field label for the name field on restaurant/bar trip item',
  },
  website: {
    id: 'ESwLUagxnXuqyhprcUSI',
    defaultMessage: 'Website',
    description: 'Form field label for website URL field on trip planner item',
  },
  reservationNumber: {
    id: 'xJJVflX7z2kQ7lGnqWj8',
    defaultMessage: 'Reservation number',
    description: 'Label for reservation number field on restaurant/bar trip item',
  },
})

const leProductMessages = defineMessages({
  villa: {
    id: 'sKqJxspAXcxPAaTqmHm0',
    defaultMessage: 'Villa',
    description: 'Product type title for Luxury Escapes villa/rental booking in trip planner',
  },
})

const itemMessages = defineMessages({
  attractionsName: {
    id: 'o48KVnuquNSOIXnq80BA',
    defaultMessage: 'Attractions',
    description: 'Item type plural name for attractions in trip planner',
  },
  attractionSingular: {
    id: '2a9HhbKTNte8EhWfHNZB',
    defaultMessage: 'Attraction',
    description: 'Item type singular name for attraction in trip planner',
  },
  carRentalName: {
    id: 'e8qM6eTe9Cwh6UXlOXwR',
    defaultMessage: 'Car Rental',
    description: 'Item type plural name for car rental in trip planner',
  },
  eventsName: {
    id: '1XNhqNb-79IAg8Y5vlOf',
    defaultMessage: 'Events',
    description: 'Item type plural name for events in trip planner in the trip planner',
  },
  eventSingular: {
    id: 've28Z6OdKn29JzFwhyTZ',
    defaultMessage: 'Event',
    description: 'Item type singular name for event in trip planner in the trip planner',
  },
  restaurantsAndBarsName: {
    id: 'oRRUQHmDwoh8pOhkYTj7',
    defaultMessage: 'Restaurants & Bars',
    description: 'Item type plural name for restaurants and bars in trip planner',
  },
  restaurantBarSingular: {
    id: '_DYoYmPahZv3dzsg6oVc',
    defaultMessage: 'Restaurant/Bar',
    description: 'Item type singular name for restaurant/bar in trip planner',
  },
  busesName: {
    id: '2BApbyR7g5TjroQ2JZPK',
    defaultMessage: 'Buses',
    description: 'Item type plural name for buses in trip planner in the trip planner',
  },
  busSingular: {
    id: 'HcRBHAB2Lgjc_RN8SjAP',
    defaultMessage: 'Bus',
    description: 'Item type singular name for bus in trip planner in the trip planner',
  },
  ferriesName: {
    id: '6kYS_hr2mS6ecJo8w5eA',
    defaultMessage: 'Ferries',
    description: 'Item type plural name for ferries in trip planner in the trip planner',
  },
  ferrySingular: {
    id: 'fHlrhY5c04Yn4P6xkauw',
    defaultMessage: 'Ferry',
    description: 'Item type singular name for ferry in trip planner in the trip planner',
  },
  privateTransportName: {
    id: 'lv7GevLHwx6vzp66GGxo',
    defaultMessage: 'Private Transport',
    description: 'Item type name for private transport in trip planner',
  },
  privateTransportSingular: {
    id: 'fPexxHkJK2ePotz50AYc',
    defaultMessage: 'Private Transport',
    description: 'Item type singular name for private transport in trip planner',
  },
  otherName: {
    id: '40MTqj-2JS9Ko3-LiVk9',
    defaultMessage: 'Other',
    description: 'Item type plural name for miscellaneous things to do in trip planner',
  },
  thingToDoSingular: {
    id: 'ik6rtgT_GS7sCz6nJR9v',
    defaultMessage: 'Thing to do',
    description: 'Item type singular name for miscellaneous activity in trip planner',
  },
  trainsName: {
    id: 'HzZs0oKBH-Lc9uAuui2w',
    defaultMessage: 'Trains',
    description: 'Item type plural name for trains in trip planner in the trip planner',
  },
  trainSingular: {
    id: '0eWJhvUwTSSQ7puloVZ1',
    defaultMessage: 'Train',
    description: 'Item type singular name for train in trip planner in the trip planner',
  },
  notesName: {
    id: 't7WYMsXJBsUCI4auOVu0',
    defaultMessage: 'Notes',
    description: 'Item type plural name for notes in trip planner in the trip planner',
  },
  noteSingular: {
    id: 'xXsORyXFwHQccFuykDRo',
    defaultMessage: 'Note',
    description: 'Item type singular name for note in trip planner in the trip planner',
  },
})

const filterMessages = defineMessages({
  stays: {
    id: 'yIyhFYv673m893pg8ePe',
    defaultMessage: 'Stays',
    description: 'Filter label for stays/accommodation in trip planner',
  },
  transport: {
    id: '2a7zooIgT9Jx_qLWVz1P',
    defaultMessage: 'Transport',
    description: 'Filter label for transport items in trip planner in the trip planner',
  },
  thingsToDo: {
    id: 'EHdcPHlNr5AsQkTvDy2q',
    defaultMessage: 'Things to do',
    description: 'Filter label for activities/things to do in trip planner',
  },
  restaurants: {
    id: 'uiDhHUXFBqbMe-wUOfMY',
    defaultMessage: 'Restaurants',
    description: 'Filter label for restaurants in trip planner in the trip planner',
  },
})

export const ParentItemMetadata: Record<ParentItemType, ParentItemViewModel> = {
  LE_BOOKINGS: {
    name: parentItemMessages.leBookingsName,
    icon: LeIcon,
  },
  THINGS_TO_DO: {
    name: parentItemMessages.placeName,
    icon: LineMapMarkerIcon,
    description: parentItemMessages.placeDescription,
  },
  FLIGHT: {
    name: productLabels.flight,
    icon: LinePlaneFlyIcon,
  },
  ACCOMMODATION: {
    name: productLabels.accommodation,
    icon: LineBedIcon,
  },
  TOUR_AND_CRUISES: {
    name: productLabels.toursAndCruises,
    icon: LineCompassIcon,
    description: parentItemMessages.tourAndCruisesDescription,
  },
  TOURS: {
    name: productLabels.tour,
    icon: LineCompassIcon,
    description: parentItemMessages.tourDescription,
  },
  CRUISES: {
    name: productLabels.cruise,
    icon: LineShipIcon,
  },
  CAR: {
    name: parentItemMessages.carRentalName,
    icon: LineCarIcon,
    description: parentItemMessages.carRentalDescription,
  },
  TRANSPORT: {
    name: parentItemMessages.transportName,
    icon: LineCarIcon,
    description: parentItemMessages.transportDescription,
  },
  NOTE: {
    name: parentItemMessages.noteName,
    icon: LineNotesIcon,
  },
}

const baseLabels: ItemViewModel['labels'] = {
  notes: labelMessages.additionalNotes,
}

export const LeItemProductTitle: Record<
  Exclude<LETripItem['sourceType'], 'NA' | 'Explore'>,
  MessageDescriptor
> = {
  LE_Cruise: productLabels.cruise,
  LE_Experience: productLabels.experience,
  LE_Flight: productLabels.flight,
  LE_Hotel: productLabels.hotel,
  LE_Hotel_Bedbank: productLabels.hotel,
  LE_Tour: productLabels.tour,
  LE_Accommodation_Rental: leProductMessages.villa,
}

export const ItemMetadata: Record<ItemType, ItemViewModel> = {
  ACCOMMODATION: {
    name: productLabels.accommodation,
    singularName: productLabels.accommodation,
    icon: LineBedIcon,
    labels: {
      ...baseLabels,
      place: labelMessages.placeName,
      phone: labelMessages.phoneNumber,
      confirmation: labelMessages.confirmationCode,
    },
    editable: true,
  },
  ATTRACTION: {
    name: itemMessages.attractionsName,
    singularName: itemMessages.attractionSingular,
    icon: LineUniversityIcon,
    editable: true,
  },
  CAR_RENTAL: {
    name: itemMessages.carRentalName,
    singularName: productLabels.carHire,
    icon: LineCarIcon,
    editable: true,
  },
  EVENT: {
    name: itemMessages.eventsName,
    singularName: itemMessages.eventSingular,
    icon: LineTicketIcon,
    editable: true,
  },
  EXPERIENCE: {
    name: productLabels.experiences,
    singularName: productLabels.experience,
    icon: LineTicketIcon,
    editable: false,
  },
  FLIGHT: {
    name: productLabels.flights,
    singularName: productLabels.flight,
    icon: LinePlaneFlyIcon,
    labels: {
      ...baseLabels,
      confirmation: labelMessages.confirmationCode,
    },
    editable: true,
  },
  RESTAURANT_BAR: {
    name: itemMessages.restaurantsAndBarsName,
    singularName: itemMessages.restaurantBarSingular,
    icon: LineRestaurantIcon,
    labels: {
      ...baseLabels,
      place: labelMessages.name,
      url: labelMessages.website,
      confirmation: labelMessages.reservationNumber,
    },
    editable: true,
  },
  BUS: {
    name: itemMessages.busesName,
    singularName: itemMessages.busSingular,
    icon: LineBusIcon,
    editable: true,
  },
  FERRY: {
    name: itemMessages.ferriesName,
    singularName: itemMessages.ferrySingular,
    icon: LineShipIcon,
    editable: true,
  },
  OTHER_TRANSPORT: {
    name: itemMessages.privateTransportName,
    singularName: itemMessages.privateTransportSingular,
    icon: LineCarIcon,
    editable: true,
  },
  THING_TO_DO: {
    name: itemMessages.otherName,
    singularName: itemMessages.thingToDoSingular,
    icon: LineMapPinIcon,
    editable: true,
  },
  TRAIN: {
    name: itemMessages.trainsName,
    singularName: itemMessages.trainSingular,
    icon: LineSubwayIcon,
    editable: true,
  },
  TOUR: {
    name: productLabels.tours,
    singularName: productLabels.tour,
    icon: LineCompassIcon,
    editable: true,
  },
  CRUISE: {
    name: productLabels.cruises,
    singularName: productLabels.cruise,
    icon: LineShipIcon,
    editable: true,
  },
  NOTE: {
    name: itemMessages.notesName,
    singularName: itemMessages.noteSingular,
    icon: LineNotesIcon,
    editable: true,
  },
}

export const FilterIcons: Record<
  App.TripPlannerSubFilterType,
  SvgIconComponent
> = {
  Stay: ItemMetadata.ACCOMMODATION.icon,
  Tour: ItemMetadata.TOUR.icon,
  Flight: ItemMetadata.FLIGHT.icon,
  'Thing to do': ItemMetadata.EXPERIENCE.icon,
  Restaurant: ItemMetadata.RESTAURANT_BAR.icon,
  Transport: ItemMetadata.OTHER_TRANSPORT.icon,
  Cruise: ItemMetadata.CRUISE.icon,
  Note: ItemMetadata.NOTE.icon,
}

export const FilterLabels: Record<App.TripPlannerSubFilterType, MessageDescriptor> = {
  Stay: filterMessages.stays,
  Tour: ItemMetadata.TOUR.name,
  Flight: ItemMetadata.FLIGHT.name,
  Transport: filterMessages.transport,
  'Thing to do': filterMessages.thingsToDo,
  Restaurant: filterMessages.restaurants,
  Cruise: ItemMetadata.CRUISE.name,
  Note: ItemMetadata.NOTE.name,
}

/** Don't change the analytics values, even if we change the labels elsewhere */
export const FilterAnalyticsLabels: Record<
  App.TripPlannerSubFilterType,
  string
> = {
  Stay: 'stays',
  Tour: 'tours',
  Flight: 'flights',
  Transport: 'transport',
  'Thing to do': 'things_to_do',
  Restaurant: 'restaurants',
  Cruise: 'cruises',
  Note: 'notes',
}
